import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {Box, Button, Stack, Typography} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import {DataGrid} from "@mui/x-data-grid";
import {TableSkeleton} from "../../common/skeleton/TableSkeleton";

const tableSx = {
    border: "1px solid #eee",
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .subscription-row-marked-delete": {
        bgcolor: "action.hover",
        color: "text.disabled",
        textDecoration: "line-through",
    },
};

const getActionProps = (row) => {
    if (row.isMarkedForDeletion) {
        return {
            color: "inherit",
            icon: <UndoIcon/>,
            label: "Undo",
        };
    }

    if (row.isNew) {
        return {
            color: "warning",
            icon: <RemoveCircleOutlineIcon/>,
            label: "Remove",
        };
    }

    return {
        color: "error",
        icon: <DeleteOutlineIcon/>,
        label: "Delete",
    };
};

export default function SubscriptionTable({
                                              title,
                                              columns,
                                              rows = [],
                                              onDeleteRow,
                                              loading = false,
                                          }) {
    const finalColumns = useMemo(
        () => [
            ...columns,
            {
                field: "actions",
                headerName: "ACTIONS",
                width: 120,
                sortable: false,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => {
                    const actionProps = getActionProps(params.row);

                    return (
                        <Button
                            variant="text"
                            color={actionProps.color}
                            size="small"
                            startIcon={actionProps.icon}
                            onClick={() => onDeleteRow?.(params.row)}
                        >
                            {actionProps.label}
                        </Button>
                    );
                },
            },
        ],
        [columns, onDeleteRow]
    );

    return (
        <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
                {title}
            </Typography>

            <Box sx={{width: "100%", bgcolor: "background.paper"}}>
                <DataGrid
                    autoHeight
                    rows={rows}
                    columns={finalColumns}
                    slots={{
                        loadingOverlay: TableSkeleton,
                    }}
                    slotProps={{
                        loadingOverlay: {
                            columns: finalColumns,
                        },
                    }}
                    localeText={{
                        noRowsLabel: "No subscriptions available.",
                    }}
                    disableRowSelectionOnClick
                    hideFooter
                    density="compact"
                    loading={loading}
                    getRowClassName={(params) => params.row.isMarkedForDeletion ? "subscription-row-marked-delete" : ""}
                    sx={tableSx}
                />
            </Box>
        </Stack>
    );
}

SubscriptionTable.propTypes = {
    title: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object),
    onDeleteRow: PropTypes.func,
    loading: PropTypes.bool,
};

SubscriptionTable.defaultProps = {
    rows: [],
    onDeleteRow: undefined,
    loading: false,
};
